import { mkdir } from 'node:fs/promises';
import { join } from 'node:path';
import { match } from 'variant';
import { agentConfigsTaskText, generateAgentConfigs } from '@wix/agent-configs';
import type { TFunction } from '@wix/cli-i18n';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { gitCommit, gitInit, runCommand } from '@wix/package-manager';
import type { IPackageManager, RepoType } from '@wix/package-manager';
import type { AppData } from './types.js';
import type { TemplateSource } from './parse-command-options.js';
import type { Task } from './task-runner.js';
import type { TemplateParams } from './validations/index.js';
import type { McpConfigResult } from './components/mcp/getMcpResult.js';

export interface TaskData {
  appData: AppData;
  packageManager: IPackageManager;
  repoType: RepoType;
  packageName: string;
  template: TemplateSource;
  templateParams?: TemplateParams;
  targetParentFolder: string;
  skipInstall: boolean;
  skipGit: boolean;
  mcpConfigResult: McpConfigResult;
  environmentVariables: Record<string, string>;
}

export function getTasks(
  {
    appData,
    packageManager,
    repoType,
    packageName,
    template,
    templateParams,
    targetParentFolder,
    skipInstall,
    skipGit,
    mcpConfigResult,
    environmentVariables,
  }: TaskData,
  t: TFunction
) {
  const packageFolder = join(targetParentFolder, packageName);

  const generateProject = async () => {
    try {
      await mkdir(packageFolder, { recursive: true });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCreateProjectFolder({ packageFolder }),
        cause: e,
      });
    }

    const { generateApp } = await import('./services/generator/index.js');

    await generateApp({
      packageName,
      packageFolder,
      appData,
      packageManager,
      repoType,
      template,
      templateParams,
      ideForMcp: match(mcpConfigResult, {
        Created: ({ ideForMcp }) => ideForMcp,
        Canceled: () => {},
      }),
      environmentVariables,
    });
    await packageManager.setup(packageFolder);
  };

  const initializeGit = async () => {
    try {
      await gitInit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInitializeGitRepo(),
        cause: e,
      });
    }
  };

  const installDependencies = async () => {
    try {
      await packageManager.runInstall(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToInstallDependencies(),
        cause: e,
      });
    }
  };

  const addWixSkills = async () => {
    const file = packageManager.getRunCmd();
    const args = ['wix', 'skills', 'add'];

    try {
      await runCommand(`${file} ${args.join(' ')}`, {
        cwd: packageFolder,
      });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToRunExternalCommand({
          file,
          args,
        }),
        cause: e,
      });
    }
  };

  const astroSync = async () => {
    const file = packageManager.getRunCmd();
    const args = ['astro', 'sync'];

    try {
      await runCommand(`${file} ${args.join(' ')}`, {
        cwd: packageFolder,
      });
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToRunExternalCommand({
          file,
          args,
        }),
        cause: e,
      });
    }
  };

  const commitToGit = async () => {
    try {
      await gitCommit(packageFolder);
    } catch (e) {
      throw new CliError({
        code: CliErrorCode.FailedToCommitChanges(),
        cause: e,
      });
    }
  };

  const generateProjectTask: Task = {
    action: generateProject,
    loadingText: t('create_app.generate_project.generating_files.loading'),
    successText: t('create_app.generate_project.generating_files.done'),
  };

  const initializeGitTask: Task = {
    action: initializeGit,
    loadingText: t('create_app.generate_project.init_git.loading'),
    successText: t('create_app.generate_project.init_git.done'),
  };

  const installDependenciesTask: Task = {
    action: installDependencies,
    loadingText: t(
      'create_app.generate_project.installing_dependencies.loading',
      {
        command: packageManager.getInstallCmd(),
      }
    ),
    successText: t('create_app.generate_project.installing_dependencies.done'),
  };

  const addWixSkillsTask: Task = {
    action: addWixSkills,
    loadingText: t('create_app.generate_project.adding_skills.loading'),
    successText: t('create_app.generate_project.adding_skills.done'),
  };

  const commitToGitTask: Task = {
    action: commitToGit,
    loadingText: t('create_app.generate_project.git_commit.loading'),
    successText: t('create_app.generate_project.git_commit.done'),
  };

  const generateAgentConfigsTask: Task = {
    action: () => generateAgentConfigs(packageFolder),
    loadingText: agentConfigsTaskText.loadingText,
    successText: agentConfigsTaskText.successText,
  };

  const astroSyncTask: Task = {
    action: astroSync,
    loadingText: t('create_app.generate_project.astro_sync.loading'),
    successText: t('create_app.generate_project.astro_sync.done'),
  };

  const optionalInstallAndAgentsTask = skipInstall
    ? []
    : [
        installDependenciesTask,
        addWixSkillsTask,
        generateAgentConfigsTask,
        astroSyncTask,
      ];

  return match(repoType, {
    None: () => [
      generateProjectTask,
      ...(skipGit ? [] : [initializeGitTask]),
      ...optionalInstallAndAgentsTask,
      ...(skipGit ? [] : [commitToGitTask]),
    ],
    Polyrepo: () => [generateProjectTask, ...optionalInstallAndAgentsTask],
    Monorepo: () => [generateProjectTask, ...optionalInstallAndAgentsTask],
  });
}
